/**
 * Floating contact-composer UI store — Zustand 5 + immer.
 *
 * Holds ONLY the session (which contact + which channel) and the window chrome.
 * Everything the user typed lives in the window component's own `useState` and,
 * once dirty, in the database via the autosave hooks — deliberately the
 * inverse of the voice widget, which keeps its dialer draft in its store
 * BECAUSE it unmounts its whole subtree when minimised. This window never
 * unmounts on minimise, so component state is sufficient, and the store stays
 * small enough to reason about.
 *
 * Mirrors `voice-ui.store.ts`'s "publish a request, let the mounted host consume
 * it" bridge: a quick-action button anywhere calls `openComposer` and the single
 * `ContactComposerMount` in `AppLayout` renders it.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import type {
    FloatingWindowState,
    IFloatingWindowPosition,
    IFloatingWindowSize,
} from '@/components/shared/floating-window';
import type { ContactComposeChannel } from '../components/contact-compose/types';

export interface IContactComposerSession {
    readonly contactId: string;
    /**
     * Display snapshot so the title bar can render before `useContact`
     * resolves — the same role `IDialRequest.contact` plays for the call widget.
     * The real contact still comes from TanStack Query.
     */
    readonly contactName: string;
    /**
     * Text to seed an EMPTY draft with — a booking link, say. Consumed ONCE by
     * the window (which clears it) so a re-render never re-seeds over what the
     * user has since typed, and an existing unsent draft always wins: a
     * convenience must not silently overwrite someone's words.
     */
    readonly initialBody?: string;
}

export interface IContactComposerUiState {
    /** `null` = closed. */
    session: IContactComposerSession | null;
    channel: ContactComposeChannel;
    windowState: FloatingWindowState;
    /** Where the NORMAL-size window sits. */
    position: IFloatingWindowPosition | null;
    /**
     * Where the MAXIMISED window sits, tracked separately and reset on every
     * maximise so the big view always opens centred. Sharing one slot meant a
     * window dragged into a corner while small re-opened maximised in that same
     * corner, half off-screen.
     */
    maximizedPosition: IFloatingWindowPosition | null;
    /**
     * User-dragged size of the NORMAL window, or `null` for the design size.
     * Persisted next to `position`: someone who widened the composer once meant
     * it, and re-widening it on every open is the same annoyance as re-dragging
     * it into place would be.
     */
    size: IFloatingWindowSize | null;

    openComposer: (input: IContactComposerSession & { channel: ContactComposeChannel }) => void;
    setChannel: (channel: ContactComposeChannel) => void;
    setWindowState: (state: FloatingWindowState) => void;
    setPosition: (position: IFloatingWindowPosition) => void;
    setMaximizedPosition: (position: IFloatingWindowPosition) => void;
    setSize: (size: IFloatingWindowSize | null) => void;
    /** Clears `session.initialBody` once the window has seeded the draft with it. */
    consumeInitialBody: () => void;
    closeComposer: () => void;
}

function makeInitialState(): Pick<
    IContactComposerUiState,
    'session' | 'channel' | 'windowState' | 'position' | 'maximizedPosition' | 'size'
> {
    return {
        session: null,
        channel: 'sms',
        windowState: 'normal',
        position: null,
        maximizedPosition: null,
        size: null,
    };
}

export const useContactComposerStore = create<IContactComposerUiState>()(
    persist(
        immer((set) => ({
            ...makeInitialState(),

            openComposer: (input) =>
                set((s) => {
                    s.session = {
                        contactId: input.contactId,
                        contactName: input.contactName,
                        initialBody: input.initialBody,
                    };
                    s.channel = input.channel;
                    // Always restore the chrome: a leftover 'minimized' from the
                    // previous session would make this open invisible.
                    s.windowState = 'normal';
                }),

            setChannel: (channel) =>
                set((s) => {
                    s.channel = channel;
                }),

            setWindowState: (windowState) =>
                set((s) => {
                    // Entering maximise always re-centres (null = the centred
                    // anchor); dragging from there fills the slot again.
                    if (windowState === 'maximized' && s.windowState !== 'maximized') {
                        s.maximizedPosition = null;
                    }
                    s.windowState = windowState;
                }),

            setPosition: (position) =>
                set((s) => {
                    s.position = position;
                }),

            setMaximizedPosition: (position) =>
                set((s) => {
                    s.maximizedPosition = position;
                }),

            setSize: (size) =>
                set((s) => {
                    s.size = size;
                }),

            consumeInitialBody: () =>
                set((s) => {
                    if (s.session) s.session = { ...s.session, initialBody: undefined };
                }),

            closeComposer: () =>
                set((s) => {
                    s.session = null;
                    s.windowState = 'normal';
                    s.maximizedPosition = null;
                }),
        })),
        {
            name: 'contact-composer-ui',
            version: 1,
            // Position only. `session` must NEVER persist: a reload would
            // resurrect a composer for a contact the user may no longer be able
            // to read, with an empty body and a stale name. The draft dots bring
            // the real drafts back instead. Same reasoning as the voice store's
            // deliberately transient `dialRequest`.
            partialize: (s) => ({ position: s.position, size: s.size }),
        },
    ),
);
